import { useEffect, useMemo, useState } from "react";
import {
  competenciaParaMesAno,
  formatarMoeda,
  formatarSelicPctAcum,
  labelConta,
} from "../utils/agregarPorConta.util";
import { formatarQuantidadeLancamentos } from "../utils/formatarQuantidade.util";
import { ordenarContas } from "../utils/ordenarContas.util";
import styles from "../detalhe.styles.module.css";

const COLUNAS_SORT = [
  { id: "conta", label: "Conta Contábil", num: false },
  { id: null, label: "Período no filtro", num: false },
  { id: "qtdLancamentos", label: "Lançamentos", num: true },
  { id: "base", label: "Base De Cálculo", num: true },
  { id: "selic", label: "Selic (% acum.) min / máx", num: true },
  { id: "baseSelic", label: "Base Selic", num: true },
  { id: "pis", label: "PIS", num: true },
  { id: "cofins", label: "COFINS", num: true },
  // Substitui "Compõe Projeto": a conta compõe o projeto exatamente quando tem base legal.
  { id: "baseLegal", label: "Base Legal", num: false },
  // TXM-558 — projeto aprovado que utilizou a conta; só preenchido no modo consulta.
  { id: null, label: "Projeto", num: false },
  { id: null, label: "Ações", num: false },
];

const PAGE_SIZES = [10, 50, 100];

function selicTitle(row) {
  const { taxaSelicMin: min, taxaSelicMax: max } = row;
  if (min == null && max == null) return undefined;
  if (min === max) return `Selic acumulada por competência no agrupamento: ${formatarSelicPctAcum(min)}`;
  return `Selic acumulada por competência no agrupamento: ${formatarSelicPctAcum(min)} – ${formatarSelicPctAcum(max)}`;
}

function renderSelicCell(row) {
  const { taxaSelicMin: min, taxaSelicMax: max } = row;
  if (min == null && max == null) return "—";
  if (min === max) {
    return <span className={styles.selicMinMax}>{formatarSelicPctAcum(min)}</span>;
  }
  return (
    <>
      <span className={styles.selicMinMax}>
        <span className={styles.selicRangeSep}>mín.</span> {formatarSelicPctAcum(min)}
      </span>
      <span className={styles.selicMinMax}>
        <span className={styles.selicRangeSep}>máx.</span> {formatarSelicPctAcum(max)}
      </span>
    </>
  );
}

export default function TabelaContas({
  contas,
  totalLancamentos,
  basesLegais = [],
  baseLegalSalvando = {},
  podeEditar = false,
  somenteLeitura = false,
  onBaseLegalChange,
  onDetalhe,
}) {
  const [sortCol, setSortCol] = useState("conta");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const ordenadas = useMemo(
    () => ordenarContas(contas, sortCol, sortDir),
    [contas, sortCol, sortDir]
  );

  const contasFingerprint = useMemo(
    () =>
      contas
        .map(
          (c) =>
            `${c.ecdContaContabil || ""}:${c.competenciaMin || ""}:${c.competenciaMax || ""}:${c.qtdLancamentos || 0}`
        )
        .join("|"),
    [contas]
  );

  useEffect(() => {
    setPage(1);
  }, [contasFingerprint, pageSize]);

  const total = ordenadas.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * pageSize;
  const slice = ordenadas.slice(start, start + pageSize);

  const handleSort = (col) => {
    if (!col) return;
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handlePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  if (!total) {
    return <p className={styles.emptyMsg}>Nenhuma conta encontrada com os filtros.</p>;
  }

  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUNAS_SORT.map((col) => {
                const sortable = Boolean(col.id);
                const sorted = sortable && sortCol === col.id;
                const thClass = [
                  col.num ? styles.num : "",
                  sortable ? styles.sortable : "",
                  sorted && sortDir === "asc" ? styles.sortedAsc : "",
                  sorted && sortDir === "desc" ? styles.sortedDesc : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <th
                    key={col.label}
                    className={thClass || undefined}
                    scope="col"
                    onClick={sortable ? () => handleSort(col.id) : undefined}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.map((row) => {
              // TXM-325 — a base legal é persistida por código no backend (não por
              // código+descrição); usa só o código para o seletor/loading-state, mas uma
              // chave composta pra linha (React key), já que a mesma conta pode aparecer
              // mais de uma vez na tabela com descrições diferentes.
              const codigoConta = row.ecdContaContabil || row.contaContabil;
              const chave = `${codigoConta}||${row.contaContabil || ""}`;
              const periodo =
                row.competenciaMin === row.competenciaMax || !row.competenciaMax
                  ? competenciaParaMesAno(row.competenciaMin)
                  : `${competenciaParaMesAno(row.competenciaMin)} – ${competenciaParaMesAno(row.competenciaMax)}`;
              return (
                <tr key={chave}>
                  <td>
                    <span className={styles.contaLabelRow}>
                      {labelConta(row.ecdContaContabil, row.contaContabil)}
                      {row.contaResultado ? (
                        <span className={styles.badgeContaResultado} title="Conta de Resultado (ECD)">
                          Resultado
                        </span>
                      ) : null}
                    </span>
                    {row.qtdMeses > 0 && (
                      <span className={styles.contaHint}>
                        {row.qtdMeses} competência{row.qtdMeses !== 1 ? "s" : ""} agregada
                        {row.qtdMeses !== 1 ? "s" : ""}
                      </span>
                    )}
                  </td>
                  <td>{periodo}</td>
                  <td className={styles.num}>{formatarQuantidadeLancamentos(row.qtdLancamentos)}</td>
                  <td className={styles.num}>{formatarMoeda(row.base)}</td>
                  <td className={`${styles.num} ${styles.selicRange}`} title={selicTitle(row)}>
                    {renderSelicCell(row)}
                  </td>
                  <td className={styles.num}>{formatarMoeda(row.baseSelic)}</td>
                  <td className={styles.num}>{formatarMoeda(row.pis)}</td>
                  <td className={styles.num}>{formatarMoeda(row.cofins)}</td>
                  <td>
                    <select
                      className={styles.selBaseLegal}
                      aria-label="Base legal"
                      title={row.baseLegalDescricao || "Sem base legal"}
                      value={row.baseLegalId == null ? "" : String(row.baseLegalId)}
                      disabled={!podeEditar || somenteLeitura || Boolean(baseLegalSalvando[codigoConta])}
                      onChange={(e) => {
                        const valor = e.target.value;
                        onBaseLegalChange?.(codigoConta, valor === "" ? null : Number(valor));
                      }}
                    >
                      <option value="">Selecione</option>
                      {/* Base legal fora do catálogo (desativada ou removida): mantém a
                          opção para não zerar silenciosamente a seleção da conta. */}
                      {row.baseLegalId != null &&
                        !basesLegais.some((b) => b.id === row.baseLegalId) && (
                          <option value={String(row.baseLegalId)}>
                            {row.baseLegalDescricao || `Base Legal #${row.baseLegalId}`}
                          </option>
                        )}
                      {basesLegais.map((b) => (
                        <option key={b.id} value={String(b.id)}>
                          {b.descricao}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{row.projetoCodigo || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnMini}
                      onClick={() => onDetalhe?.(row)}
                    >
                      Detalhes
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.paginationBar}>
        <span className={styles.paginationInfo}>
          {totalLancamentos > 0 && `${totalLancamentos.toLocaleString("pt-BR")} lançamentos no resultado · `}
          Exibindo {start + 1}–{Math.min(start + pageSize, total)} de {total} contas · Página{" "}
          {pageSafe} de {totalPages}
        </span>
        <div className={styles.paginationControls}>
          <label htmlFor="contasPageSize">Contas por página</label>
          <select
            id="contasPageSize"
            className={styles.pageSizeSelect}
            value={pageSize}
            onChange={handlePageSize}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.btnPage}
            disabled={pageSafe <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <button
            type="button"
            className={styles.btnPage}
            disabled={pageSafe >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Próxima
          </button>
        </div>
      </div>
    </>
  );
}
